import React,{useContext} from 'react';
import { 
  Box, Typography, IconButton, ToggleButton, ToggleButtonGroup, Button, 
  Tooltip
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import FilterListIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import RefreshIcon from '@mui/icons-material/Refresh'; 
import PrintIcon from '@mui/icons-material/Print';
import PivotTableChartIcon from '@mui/icons-material/PivotTableChart';
import CodeIcon from '@mui/icons-material/Code';
import ApiIcon from '@mui/icons-material/Api';
import AuthContext from "./auth/Context";
export default function ReportHeader({ 
  code,
  title, 
  onBack, 
  onOpenFilter,
  onOpenColumnSettings,
  onRefresh,    
  isRefreshing, 
  onPrint,          
  viewMode,         
  onViewModeChange,
  enableCustomView = false, // hiện thêm nút "Giao diện tuỳ chỉnh" trong nhóm chế độ xem
  onOpenCustomView          // mở trình soạn thảo script (bấm lại nút custom khi đang ở chế độ custom)
}) {
  const context = useContext(AuthContext);
  return (
    <Box 
      sx={{ 
        bgcolor: 'primary.main', color: 'primary.contrastText',
        px: 1.5, flexShrink: 0,
        pt: 'max(12px,env(safe-area-inset-top))', 
        pb: 1.5,
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' }, 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        justifyContent: 'space-between', 
        gap: { xs: 1.5, sm: 0 } 
      }}
    >
      {/* KHU VỰC TRÁI: NÚT BACK VÀ TIÊU ĐỀ */}
      <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden', width: { xs: '100%', sm: 'auto' } }}>
        {onBack && (
          <Tooltip title="Quay lại" placement="bottom">
            <IconButton onClick={onBack} sx={{ color: 'inherit', mr: 0.5, p: 1, ml: -1 }}>
              <ArrowBackIosNewIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        )}
        <Typography variant="h6" fontWeight="bold" noWrap sx={{ fontSize: '1.1rem' }}>
          {title}
        </Typography>
      </Box>
      
      {/* KHU VỰC PHẢI: CÁC NÚT CÔNG CỤ */}
      <Box sx={{ 
        display: 'flex', alignItems: 'center', gap: 1, 
        width: { xs: '100%', sm: 'auto' }, 
        justifyContent: { xs: 'flex-end', sm: 'flex-end' },
        overflowX: 'auto', 
        '&::-webkit-scrollbar': { display: 'none' },

        '& .MuiIconButton-root, & .MuiButton-root': {
          borderRadius: 1, 
          border: '1px solid rgba(255, 255, 255, 0.3)',
          minWidth: 0,
          width: 32,
          height: 32,
          padding: 0,
          color: 'rgba(255, 255, 255, 0.7)',
          bgcolor: 'transparent',
          transition: 'all 0.2s ease',
          '&:hover': {
            color: 'white',
            borderColor: 'rgba(255, 255, 255, 0.5)',
            bgcolor: 'rgba(255, 255, 255, 0.1)'
          },
          '&.Mui-disabled': { 
            color: 'rgba(255, 255, 255, 0.3)', 
            borderColor: 'rgba(255, 255, 255, 0.1)' 
          }
        }
      }}>
        
        {/* --- 5. NÚT LỌC (FILTER) --- */}
        {onOpenFilter && (
          <Tooltip title="Bộ lọc" placement="bottom">
            <span style={{ display: 'inline-flex' }}>
              <Button variant="outlined" onClick={onOpenFilter}>
                <FilterListIcon fontSize="small" />
              </Button>
            </span>
          </Tooltip>
        )}

        {/* --- 1. NÚT LÀM MỚI (REFRESH) --- */}
        {onRefresh && (
          <Tooltip title="Làm mới dữ liệu" placement="bottom">
            {/* Dùng <span> bọc ngoài Button disabled để Tooltip vẫn hoạt động */}
            <span style={{ display: 'inline-flex' }}>
              <Button variant="outlined" onClick={onRefresh} disabled={isRefreshing}>
                <RefreshIcon 
                  fontSize="small" 
                  sx={{ animation: isRefreshing ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} 
                />
              </Button>
            </span>
          </Tooltip>
        )}

        {/* --- 2. NÚT IN ẤN --- */}
        {onPrint && (
          <Tooltip title="In ấn & Xuất Excel" placement="bottom">
            <span style={{ display: 'inline-flex' }}>
              <Button variant="outlined" onClick={onPrint}>
                <PrintIcon fontSize="small" />
              </Button>
            </span>
          </Tooltip>
        )}

        {/* --- 3. NÚT CHUYỂN ĐỔI CHẾ ĐỘ HIỂN THỊ --- */}
        {onViewModeChange && (
          <ToggleButtonGroup
            size="small" value={viewMode || 'table'} exclusive onChange={(e, newVal) => { if (newVal) onViewModeChange(newVal); }}
            sx={{ 
                height: 32,
                '& .MuiToggleButton-root': { 
                    color: 'rgba(255, 255, 255, 0.7)', 
                    borderColor: 'rgba(255, 255, 255, 0.3)', 
                    px: 1.2,
                    '&.Mui-selected': { color: 'white', bgcolor: 'rgba(255, 255, 255, 0.2)', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' } } 
                } 
            }}
          >
            <Tooltip title="Dạng bảng" placement="bottom">
              <ToggleButton value="table"><ViewListIcon fontSize="small" /></ToggleButton>
            </Tooltip>
            
            <Tooltip title="Dạng thẻ (Card)" placement="bottom">
              <ToggleButton value="card"><GridViewIcon fontSize="small" /></ToggleButton>
            </Tooltip>

            <Tooltip title="Bảng tổng hợp (Pivot)" placement="bottom">
              <ToggleButton value="pivot"><PivotTableChartIcon fontSize="small" /></ToggleButton>
            </Tooltip>

            {enableCustomView && (
              <Tooltip title={viewMode === 'custom' ? 'Soạn thảo giao diện tuỳ chỉnh' : 'Giao diện tuỳ chỉnh (Script)'} placement="bottom">
                {/* Đang ở chế độ custom mà bấm lại => mở luôn trình soạn thảo (ToggleButton không tự bắn onChange khi bấm lại chính nó) */}
                <ToggleButton value="custom" onClick={() => { if (viewMode === 'custom' && onOpenCustomView) onOpenCustomView(); }}>
                  <CodeIcon fontSize="small" />
                </ToggleButton>
              </Tooltip>
            )}
          </ToggleButtonGroup>
        )}

        {/* --- 4. NÚT TÙY CHỈNH CỘT --- */}
        {onOpenColumnSettings && (
          <Tooltip title="Ẩn/Hiện cột báo cáo" placement="bottom">
            <span style={{ display: 'inline-flex' }}>
              <Button variant="outlined" onClick={onOpenColumnSettings}>
                <ViewColumnIcon fontSize="small" />
              </Button>
            </span>
          </Tooltip>
        )}

        

        {/* --- NÚT XEM TÀI LIỆU API --- */}
        {code && (
          <Tooltip title="Tài liệu API" placement="bottom">
            <span style={{ display: 'inline-flex' }}>
              <Button 
                variant="outlined" 
                onClick={() => window.open(`${context.config?.server_url}/api/docs/report/${code}?id_app=${context.appInfo?._id}`, '_blank')}
              >
                <ApiIcon fontSize="small" />
              </Button>
            </span>
          </Tooltip>
        )}

      </Box>
    </Box>
  );
}